import { registerUserAction } from "@/app/actions";
import { EfnAuth } from "@/services/auth/types";
import { getCaptchaToken } from "@/services/google";
import { loadScript } from "@/utils/scripts";
import Link from "next/link";
import { useState } from "react";
import { useFormState } from "react-dom";
import Button from "../button";
import FormErrorSummary from "../form/form-error-summary";
import InputCheckbox from "../form/input-checkbox";
import InputField from "../form/input-field";
import InputGroup from "../form/input-group";
import InputPhone from "../form/input-phone";
import RequiredFieldsNote from "../form/required-fields-note";
import InfoHeader from "./info-header";

type RegisterProps = {
  onSuccess: (auth: EfnAuth.RegisterResponse) => void;
  onError: (message: string) => void;
  email: string;
  isApp: boolean;
};

const Register = ({
  onSuccess,
  onError,
  email,
  isApp = false,
}: RegisterProps) => {
  const [hasInvalidFields, setHasInvalidFields] = useState(false);
  const [res, action]: any = useFormState(async (state: any, formData: any) => {
    setHasInvalidFields(false);
    const siteKey = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY;
    let token = "";

    if (siteKey) {
      await loadScript({
        src: `https://www.google.com/recaptcha/api.js?render=${siteKey}`,
        async: true,
        defer: true,
        waitFor: () =>
          new Promise<void>((resolve, reject) => {
            const test = (tries: number) => {
              if (typeof (window as any)?.grecaptcha?.execute !== "undefined") {
                resolve();
              } else if (tries > 40) {
                reject();
              } else {
                setTimeout(() => test(tries + 1), tries < 10 ? 100 : 350);
              }
            };

            test(0);
          }),
      });

      token = await getCaptchaToken("submit_login", siteKey);
      if (!token) return;
    }
    else {
      token = "captcha-disabled";
    }

    const countryCode = formData.get("countryCode")?.toString() || "";
    const phone = formData.get("phone")?.toString() || "";

    const completePhone = phone
      ? `${countryCode}${phone.startsWith("0") ? phone.slice(1) : phone}`
      : undefined;

    const user: EfnAuth.RegisterPayload = {
      firstname: formData.get("firstname").toString(),
      lastname: formData.get("lastname").toString(),
      phone: completePhone,
      toc_accepted: Boolean(formData.get("accept-toc")) || false,
      email,
      token,
      isapp: isApp,
    };
    if (!user.toc_accepted)
      return onError(
        "Du måste godkänna användarvillkoren för att skapa ett konto"
      );
    const res = await registerUserAction(user);
    if (res?.success) return onSuccess(res);
    onError("Kunde inte skapa konto");
  }, null);

  return (
    <>
      <InfoHeader
        header="Välkommen!"
        description="För att kunna skapa ett konto åt dig behöver vi bara några ytterligare uppgifter."
      />
      <RequiredFieldsNote />
      <form
        className="space-y-6"
        action={action}
        onInvalidCapture={() => setHasInvalidFields(true)}
      >
        <FormErrorSummary hasError={hasInvalidFields} />
        <InputGroup className="grid grid-cols-2 gap-4">
          <InputField
            label="Förnamn"
            type="text"
            placeholder="Ange förnamn"
            name="firstname"
            required
            autoComplete="given-name"
          />
          <InputField
            label="Efternamn"
            type="text"
            placeholder="Ange efternamn"
            name="lastname"
            required
            autoComplete="family-name"
          />
          <InputPhone containerClassName="col-span-2" />
        </InputGroup>
        <InputGroup className="text-gray-400">
          <InputCheckbox defaultValue={false} name="accept-toc" required>
            <p className="ml-4 font-semibold leading-tight">
              Jag godkänner{" "}
              <Link
                href="/anvandarvillkor"
                target="_blank"
                className="text-blue-link underline"
              >
                användarvillkoren
              </Link>{" "}
              och bekräftar att jag tagit del av EFNs{" "}
              <Link
                href="/integritetspolicy"
                target="_blank"
                className="text-blue-link underline"
              >
                integritetspolicy
              </Link>
              . EFN ansvarar för kundrelationen samt för behandlingen av dina
              personuppgifter.
            </p>
          </InputCheckbox>
        </InputGroup>
        <Button
          type="submit"
          showLoader
          className="btn btn-primary h-10 px-16 w-full self-end"
        >
          Skapa konto
        </Button>
      </form>
    </>
  );
};

export default Register;
